import { marketingConsentGranted } from '@/lib/marketingConsent';
import mixpanel from './posthog';
import { mirrorEventToGA } from './googleAnalytics';
import { oaiqIdentifyUser, oaiqSignupCompleted, oaiqSubscriptionCreated } from './openaiAds';

// Compatibility exports for existing callers; PostHog owns ingestion and replay.
// Event Names - Centralized event tracking constants
export const MIXPANEL_EVENTS = {
  // Auth Events
  SIGN_UP_STARTED: 'Sign Up Started',
  SIGN_UP_COMPLETED: 'Sign Up Completed',
  SIGN_IN_STARTED: 'Sign In Started',
  SIGN_IN_COMPLETED: 'Sign In Completed',
  SIGN_OUT: 'Sign Out',
  
  // Onboarding Events
  ONBOARDING_STARTED: 'Onboarding Started',
  ONBOARDING_STEP_COMPLETED: 'Onboarding Step Completed',
  ONBOARDING_COMPLETED: 'Onboarding Completed',
  PROFILE_IMAGE_UPLOADED: 'Profile Image Uploaded',
  
  // Learning Events
  LEARNING_JOURNEY_STARTED: 'Learning Journey Started',
  UNIT_STARTED: 'Unit Started',
  UNIT_COMPLETED: 'Unit Completed',
  MODULE_STARTED: 'Module Started',
  MODULE_COMPLETED: 'Module Completed',
  LESSON_STARTED: 'Lesson Started',
  LESSON_COMPLETED: 'Lesson Completed',
  
  // Quiz Events
  QUIZ_STARTED: 'Quiz Started',
  QUIZ_QUESTION_ANSWERED: 'Quiz Question Answered',
  QUIZ_COMPLETED: 'Quiz Completed',
  QUIZ_PASSED: 'Quiz Passed',
  QUIZ_FAILED: 'Quiz Failed',
  
  // Content Events
  DOC_VIEWED: 'Documentation Viewed',
  BLOG_POST_VIEWED: 'Blog Post Viewed',
  VIDEO_WATCHED: 'Video Watched',
  CODE_EXAMPLE_VIEWED: 'Code Example Viewed',
  
  // Engagement Events
  BUTTON_CLICKED: 'Button Clicked',
  LINK_CLICKED: 'Link Clicked',
  SEARCH_PERFORMED: 'Search Performed',
  FEEDBACK_SUBMITTED: 'Feedback Submitted',
  
  // Checkout & Payment Events — snake_case matches the live onboarding
  // taxonomy (onboarding_*). The old TitleCase names ('Checkout Started')
  // stopped firing when the legacy flow died; every surface now reports
  // through these. `purchase_confirmed` is the server-side twin fired by
  // stripe-webhook-inc so a closed tab can't lose the conversion.
  PRICING_PAGE_VIEWED: 'Pricing Page Viewed',
  PAYWALL_VIEWED: 'paywall_viewed',
  PAYWALL_CTA_CLICKED: 'paywall_cta_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_ABANDONED: 'checkout_abandoned',
  CHECKOUT_COMPLETED: 'checkout_completed',
  SUBSCRIPTION_UPGRADED: 'Subscription Upgraded',
  SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',
  
  // Certificate Events
  CERTIFICATE_EARNED: 'Certificate Earned',
  CERTIFICATE_VIEWED: 'Certificate Viewed',
  CERTIFICATE_SHARED: 'Certificate Shared',
  
  // Community Events
  COMMUNITY_VISITED: 'Community Page Visited',

  // Studio/Cody Events
  STUDIO_OPENED: 'Studio Opened',
  PROMPT_SUBMITTED: 'Prompt Submitted',
  PROMPT_LAB_USED: 'Prompt Lab Used',
  
  // Error Events
} as const;

// User Properties
export interface MixpanelUserProperties {
  $name?: string;
  $email?: string;
  $avatar?: string;
  $created?: string;
  created_at?: string;
  plan?: string;
  subscription_tier?: string;
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: string;
  subscriptionPlan?: string;
  subscriptionStartDate?: string;
  buildGoal?: string;
  codingExperience?: string;
  referralSource?: string;
  learningLevel?: string;
  learningJourneyProgress?: number;
  totalUnitsCompleted?: number;
  totalQuizzesCompleted?: number;
  totalCertificatesEarned?: number;
  totalPurchases?: number;
  last_seen?: string;
  lastPurchaseDate?: string;
  firstPageViewTime?: string;
}

/**
 * Initialize super properties that persist across all events
 */
export const initializeSuperProperties = () => {
  mixpanel.register_once({
    firstPageViewTime: new Date().toISOString(),
  });
  
  mixpanel.register({
    platform: 'web',
    appVersion: '1.0.0',
  });
};

/**
 * Identify a user and set their ID
 */
export const identifyUser = (userId: string) => {
  mixpanel.identify(userId);
};

/**
 * One-stop identity sync: identify + $email/$name/$avatar from the Supabase
 * auth user. Called on every SIGNED_IN/INITIAL_SESSION so profiles converge
 * even for users created before this existed. people.set is idempotent.
 */
export const syncUserIdentity = (user: {
  id: string;
  email?: string | null;
  created_at?: string;
  user_metadata?: Record<string, any> | null;
}) => {
  if (!user?.id) return;
  // OpenAI Ads user matching — hashed email/id on every session so pixel
  // conversions later in the session (checkout, lessons) match the user.
  oaiqIdentifyUser({ userId: user.id, email: user.email });
  {
    const md = user.user_metadata || {};
    const name = String(
      md.full_name || md.name || [md.first_name, md.last_name].filter(Boolean).join(' ') || ''
    ).trim();
    mixpanel.identify(user.id);
    const props: MixpanelUserProperties = { last_seen: new Date().toISOString() };
    if (user.email) props.$email = user.email;
    if (name) props.$name = name;
    if (md.avatar_url) props.$avatar = md.avatar_url;
    mixpanel.people.set(props);
    if (user.created_at) {
      mixpanel.people.set_once({ created_at: user.created_at });
    }
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: MixpanelUserProperties) => {
  mixpanel.people.set(properties);
};

/**
 * Increment a user property (useful for counters)
 */
export const incrementUserProperty = (property: string, value: number = 1) => {
  mixpanel.people.increment(property, value);
};

/**
 * Set user properties only once (won't overwrite existing values)
 */
export const setUserPropertiesOnce = (properties: MixpanelUserProperties) => {
  mixpanel.people.set_once(properties);
};

/**
 * Track an event with properties
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  const eventProperties = {
    timestamp: new Date().toISOString(),
    ...properties,
  };

  mixpanel.track(eventName, eventProperties);
  // Funnel events are mirrored to GA4/Google Ads (see googleAnalytics.ts).
  mirrorEventToGA(eventName, properties);
};

/**
 * Track page view with route information
 */
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  // One page event: PostHog's $pageview. The custom 'Page Viewed' twin is gone.
  mixpanel.track('$pageview', { ...properties, pageName, path: window.location.pathname, $pathname: window.location.pathname });
};

/**
 * Start timing an event (useful for measuring duration)
 */
export const startTimingEvent = (eventName: string) => {
  mixpanel.time_event(eventName);
};

/**
 * Track an event with automatic duration calculation
 * Call startTimingEvent first, then trackTimedEvent when complete
 */
export const trackTimedEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  trackEvent(eventName, properties);
};

/**
 * Calculate time from first page view to a specific event
 */
export const getTimeFromFirstPageView = (): number | null => {
  const firstPageViewTime = mixpanel.get_property('firstPageViewTime');
  if (!firstPageViewTime) return null;
  
  const firstView = new Date(firstPageViewTime);
  const now = new Date();
  return (now.getTime() - firstView.getTime()) / 1000; // Return in seconds
};

/**
 * Track checkout completion with time-to-conversion
 */
export const trackCheckoutCompleted = (properties?: Record<string, any>) => {
  const timeToConversion = getTimeFromFirstPageView();
  
  trackEvent(MIXPANEL_EVENTS.CHECKOUT_COMPLETED, {
    timeToConversion,
    ...properties,
  });

  // OpenAI Ads conversion — webhook-confirmed subscription (all surfaces
  // report checkout success through this helper).
  if (properties?.receipt_verified === true && properties?.marketing_consent === 'granted' && marketingConsentGranted()) oaiqSubscriptionCreated({
    transactionId: typeof properties?.transaction_id === 'string' ? properties.transaction_id : undefined,
    tier: typeof properties?.tier === 'string' ? properties.tier : undefined,
    plan: typeof properties?.plan === 'string' ? properties.plan : undefined,
    valueUsd: typeof properties?.value === 'number' ? properties.value : undefined,
    userId: typeof properties?.userId === 'string' ? properties.userId : undefined,
  });

  // Update user properties
  incrementUserProperty('totalPurchases', 1);
  setUserProperties({
    lastPurchaseDate: new Date().toISOString(),
  });
};

/**
 * Track learning progress events
 */
export const trackLearningProgress = {
  unitStarted: (unitId: string, unitName: string, sectionId?: string) => {
    startTimingEvent(MIXPANEL_EVENTS.UNIT_COMPLETED);
    trackEvent(MIXPANEL_EVENTS.UNIT_STARTED, {
      unitId,
      unitName,
      sectionId,
    });
  },
  
  unitCompleted: (unitId: string, unitName: string, sectionId?: string) => {
    trackTimedEvent(MIXPANEL_EVENTS.UNIT_COMPLETED, {
      unitId,
      unitName,
      sectionId,
    });
    incrementUserProperty('totalUnitsCompleted', 1);
  },
  
  moduleStarted: (moduleId: string, moduleName: string, unitId?: string) => {
    startTimingEvent(MIXPANEL_EVENTS.MODULE_COMPLETED);
    trackEvent(MIXPANEL_EVENTS.MODULE_STARTED, {
      moduleId,
      moduleName,
      unitId,
    });
  },
  
  moduleCompleted: (moduleId: string, moduleName: string, unitId?: string) => {
    trackTimedEvent(MIXPANEL_EVENTS.MODULE_COMPLETED, {
      moduleId,
      moduleName,
      unitId,
    });
  },
  
  lessonStarted: (lessonId: string, lessonName: string, moduleId?: string) => {
    startTimingEvent(MIXPANEL_EVENTS.LESSON_COMPLETED);
    trackEvent(MIXPANEL_EVENTS.LESSON_STARTED, {
      lessonId,
      lessonName,
      moduleId,
    });
  },
  
  lessonCompleted: (lessonId: string, lessonName: string, moduleId?: string) => {
    trackTimedEvent(MIXPANEL_EVENTS.LESSON_COMPLETED, {
      lessonId,
      lessonName,
      moduleId,
    });
  },
};

/**
 * Track quiz events
 */
export const trackQuizEvents = {
  started: (quizId: string, quizName: string, totalQuestions: number) => {
    startTimingEvent(MIXPANEL_EVENTS.QUIZ_COMPLETED);
    trackEvent(MIXPANEL_EVENTS.QUIZ_STARTED, {
      quizId,
      quizName,
      totalQuestions,
    });
  },
  
  questionAnswered: (
    quizId: string,
    questionNumber: number,
    isCorrect: boolean,
    timeTaken?: number
  ) => {
    trackEvent(MIXPANEL_EVENTS.QUIZ_QUESTION_ANSWERED, {
      quizId,
      questionNumber,
      isCorrect,
      timeTaken,
    });
  },
  
  completed: (
    quizId: string,
    quizName: string,
    score: number,
    totalQuestions: number,
    passed: boolean
  ) => {
    trackTimedEvent(MIXPANEL_EVENTS.QUIZ_COMPLETED, {
      quizId,
      quizName,
      score,
      totalQuestions,
      passed,
      percentage: (score / totalQuestions) * 100,
    });
    
    if (passed) {
      trackEvent(MIXPANEL_EVENTS.QUIZ_PASSED, { quizId, quizName, score });
    } else {
      trackEvent(MIXPANEL_EVENTS.QUIZ_FAILED, { quizId, quizName, score });
    }
    
    incrementUserProperty('totalQuizzesCompleted', 1);
  },
};

/**
 * Track auth events
 */
export type AuthMethod = 'email' | 'google' | 'github' | 'apple' | string;

export const trackAuthEvents = {
  signUpStarted: (method: AuthMethod) => {
    trackEvent(MIXPANEL_EVENTS.SIGN_UP_STARTED, { method });
  },

  signUpCompleted: (userId: string, email: string, method: AuthMethod) => {
    identifyUser(userId);
    trackEvent(MIXPANEL_EVENTS.SIGN_UP_COMPLETED, { method });
    // OpenAI Ads conversion — account creation confirmed (email + OAuth paths).
    oaiqSignupCompleted(userId, email);

    setUserPropertiesOnce({
      $email: email,
      created_at: new Date().toISOString(),
      onboardingCompleted: false,
      plan: 'free',
      subscription_tier: 'free',
    });
  },

  signInStarted: (method: AuthMethod) => {
    trackEvent(MIXPANEL_EVENTS.SIGN_IN_STARTED, { method });
  },

  signInCompleted: (userId: string, email: string, method?: AuthMethod) => {
    identifyUser(userId);
    trackEvent(MIXPANEL_EVENTS.SIGN_IN_COMPLETED, method ? { method } : undefined);

    setUserProperties({
      last_seen: new Date().toISOString(),
    });
  },
  
  signOut: () => {
    trackEvent(MIXPANEL_EVENTS.SIGN_OUT);
    mixpanel.reset(); // Clear user identity
  },
};

/**
 * Track onboarding events
 */
export const trackOnboardingEvents = {
  started: () => {
    startTimingEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED);
    trackEvent(MIXPANEL_EVENTS.ONBOARDING_STARTED);
  },
  
  stepCompleted: (stepNumber: number, stepName: string) => {
    trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
      stepNumber,
      stepName,
    });
  },
  
  completed: (userName: string, profileImageUrl?: string) => {
    trackTimedEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED);
    
    setUserProperties({
      $name: userName,
      $avatar: profileImageUrl,
      onboardingCompleted: true,
      onboardingCompletedAt: new Date().toISOString(),
    });
  },
  
  profileImageUploaded: () => {
    trackEvent(MIXPANEL_EVENTS.PROFILE_IMAGE_UPLOADED);
  },
};

/**
 * Track navigation events
 */
export const trackNavigation = {
  sidebarToggled: (isOpen: boolean) => {
    trackEvent('Sidebar Toggled', { isOpen });
  },
  
  searchPerformed: (query: string, resultsCount: number, location: string) => {
    trackEvent(MIXPANEL_EVENTS.SEARCH_PERFORMED, {
      query,
      resultsCount,
      location,
    });
  },
  
  sectionViewed: (sectionId: string, sectionName: string) => {
    trackEvent('Section Viewed', {
      sectionId,
      sectionName,
    });
  },
  
  moduleNavigated: (moduleId: string, moduleName: string, sectionId: string) => {
    trackEvent('Module Navigated', {
      moduleId,
      moduleName,
      sectionId,
    });
  },
};

/**
 * Track chat/Cody events
 */
export const trackCodyEvents = {
  chatOpened: (location: string, moduleTitle?: string) => {
    trackEvent('Cody Chat Opened', {
      location,
      moduleTitle,
    });
  },
  
  messageSent: (messageLength: number, location: string) => {
    trackEvent('Cody Message Sent', {
      messageLength,
      location,
    });
  },
  
  conversationStarted: (location: string) => {
    trackEvent('Cody Conversation Started', {
      location,
    });
  },
};

/**
 * Track engagement metrics
 */
export const trackEngagement = {
  buttonClick: (buttonName: string, location: string) => {
    trackEvent(MIXPANEL_EVENTS.BUTTON_CLICKED, {
      buttonName,
      location,
    });
  },
  
  linkClick: (linkText: string, linkUrl: string, location: string) => {
    trackEvent(MIXPANEL_EVENTS.LINK_CLICKED, {
      linkText,
      linkUrl,
      location,
    });
  },
  
  search: (query: string, resultsCount: number) => {
    trackEvent(MIXPANEL_EVENTS.SEARCH_PERFORMED, {
      query,
      resultsCount,
    });
  },
  
  feedbackSubmitted: (rating: number, comment?: string) => {
    trackEvent(MIXPANEL_EVENTS.FEEDBACK_SUBMITTED, {
      rating,
      comment,
    });
  },
};

/**
 * Update session engagement time
 */
export const updateSessionEngagement = () => {
  const sessionStart = sessionStorage.getItem('sessionStartTime');
  if (!sessionStart) {
    sessionStorage.setItem('sessionStartTime', new Date().toISOString());
    return;
  }
  
  const start = new Date(sessionStart);
  const now = new Date();
  const engagementTime = (now.getTime() - start.getTime()) / 1000; // in seconds
  
  mixpanel.register({
    sessionEngagementTime: engagementTime,
  });
};

/**
 * Initialize Mixpanel tracking utilities
 * Call this AFTER mixpanel.init() has been called
 */
export const initializeMixpanelTracking = () => {
  if (typeof window === 'undefined') return;
  
  try {
    // Initialize super properties
    initializeSuperProperties();
    
    // Track session engagement periodically
    setInterval(updateSessionEngagement, 30000); // Update every 30 seconds
  } catch (error) {
    console.error('Error initializing Mixpanel tracking:', error);
  }
};

export default mixpanel;

